import { type ReactNode, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { Button } from "./Button.js";

export type ConfirmDialogProps = {
  open: boolean;
  title: string;
  description?: string | undefined;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "danger" | "default";
  onConfirm: () => void;
  onCancel: () => void;
  /** An extra control under the description (e.g. a checkbox). */
  children?: ReactNode;
};

/** Themed confirmation dialog — instead of the browser `confirm()`. A controlled
 *  (`open`) component: local state in the caller chains the button → the dialog →
 *  the actual action step by step. Esc / backdrop → onCancel; on open the
 *  confirm button is focused. The overlay is portaled into body so it does not
 *  get trapped inside a filter container (backdrop-blur). */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Tasdiqlash",
  cancelLabel = "Bekor",
  tone = "default",
  onConfirm,
  onCancel,
  children,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descId = useId();

  // onCancel gets a new identity on every render (inline callback) — we read it
  // through a ref and tie the effect only to `open`. Otherwise per-second
  // re-renders such as the clock pull the focus back every second.
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (!open) return;
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancelRef.current();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  return createPortal(
    // Backdrop click → cancel. This is just a convenience shortcut; the keyboard
    // path is fully covered by Esc, so a static overlay is appropriate.
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismiss; Esc covers keyboard
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-canvas-veil p-4 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        className="w-full max-w-sm rounded-lg border border-border bg-surface p-5 shadow-[0_20px_40px_-12px_var(--p-shadow-lg)]"
      >
        <h2 id={titleId} className="text-[15px] font-semibold text-ink">
          {title}
        </h2>
        {description && (
          <p id={descId} className="mt-2 whitespace-pre-line text-[13px] text-ink-muted">
            {description}
          </p>
        )}
        {children}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            ref={confirmRef}
            variant={tone === "danger" ? "danger" : "success"}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
